import { Component, OnInit, ViewChild, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MatAccordion } from '@angular/material/expansion';
import { takeUntil } from 'rxjs/operators';
import { Finding } from '../../beans/health-summary/health-summary.module.bean';
import { ScrollToService } from '@hkjeffchan/ngx-scroll-to';
import { Subject, Observable, Subscription, timer } from 'rxjs';
import { DataCarrierService } from '../../services/core/data-carrier.service';
import { AppointmentsService } from '../../services/scheduling/appointment/appointments.service';
import { FormControl } from '@angular/forms';
import { HealthSummaryService } from 'src/app/services/health-summary/health-summary.service';

/**
 *
 * The WellnessComponent (aka Health Summary) is the main component for
 * the users lab results connecting HealthMeasureComponent, LabResultsComponent,
 * and the LifestyleComponent
 * @export
 * @class WellnessComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
	selector: 'app-wellness',
	templateUrl: './wellness.component.html',
	styleUrls: ['./wellness.component.scss'],
})
export class WellnessComponent implements OnInit, OnDestroy {
	/** Used for interacting with the health panels to open and close them */
	@ViewChild('myaccordion', { static: true }) myPanels: MatAccordion;
	/** Contains only the health measurement data from health/wellness data */
	healthMeasurementData: Finding[];
	/** Represents if there is no data for health measurements on the UI */
	healthMeasurementDataEmpty = true;
	/** Contains only the lab result data from health/wellness data */
	labResultsData: Finding[];
	/** Represents if there is no data for lab results on the UI */
	labResultsDataEmpty = true;
	/** Contains only the lifestyle data from health/wellness data */
	lifestyleData: Finding[];
	/** Represents if there is no data for lifestyle on the UI */
	lifestyleDataEmpty = true;
	/** stores the current view of the user of web vs mobile */
	isMobile: boolean;
	/**
	 * Used to calculate the current state of the user
	 * - State 0: No scheduled physical exam
	 * - State 1: Upcoming physical exam
	 * - State 2: Completed physical exam && Waiting for reports
	 * - State 3: Completed physical exam && Reports are in
	 */
	state: number;
	/**
	 * @private
	 * keeps track of when subscriptions are taken until
	 */
	private _destroyed$: Subject<boolean> = new Subject<boolean>();
	/** Active section index to handle expansion panels */
	activeSectionIndex = 0;
	/** This is here to solve an edge case related to page load in mobile */
	mobileIndex = 0;
	/** Check whether wellness data available or not */
	isEmpty = true;
	/**
	 * used as an optimization for charts when changing routes
	 * this is to avoid them rendering too often
	 */
	renderCharts = true;
	recordSection = new FormControl();
	biometricUser: boolean = false;
	eligibleForPE: boolean = false;
	// TODO: Declared but not used
	profileRelatedInfo: any;

	/**
	 * Creates an instance of WellnessComponent.
	 * @param {Router} router  Angular router
	 * @param {ScrollToService} _scrollToService  Scroll To Service handles starting, interrupting and ending the actual Scroll Animation
	 * @param {DataCarrierService} dataCarrierService  Data carrier service
	 * @param {AppointmentsService} appointmentService Appointment service
	 * @memberof WellnessComponent
	 */
	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private _scrollToService: ScrollToService,
		private dataCarrierService: DataCarrierService,
		public appointmentService: AppointmentsService,
		private healthSummaryService: HealthSummaryService
	) {
		Promise.resolve((this.isMobile = this._detectMobile())).then(() => {
			this._getActiveSection();
		});
		this.recordSection.setValue(1);
		// For biometric non-eligible user - health record is not present
		if (this.biometricUser && !this.eligibleForPE) {
			this.recordSection.setValue(2);
		}
		this.biometricUser = this.dataCarrierService.getChannel().getValue().hubbleUser;
		this.eligibleForPE = this.dataCarrierService.getChannel().getValue().eligibleForPE;
		this.activatedRoute.queryParams.subscribe(params => {
			if (params.fromBiometrics === 'true') {
				this.recordSection.setValue(2);
			} else if (params.fromBiometrics === 'false') {
				this.recordSection.setValue(1);
			}
		});
	}

	/**
	 * On component initialization
	 */
	ngOnInit(): void {
		this._getHealthSummary();
		this._getWellnessData();
	}

	/**
	 * Listens to window resize to detect mobile view
	 * @param event
	 */
	@HostListener('window:resize', ['$event'])
	onResize(event) {
		Promise.resolve((this.isMobile = this._detectMobile())).then(() => {
			this._handleRouteChange(this.router.url);
		});
	}

	/**
	 *
	 * Checks if the device is a tablet or smaller
	 * @private
	 * @returns {boolean} boolean value that detects a mobile device
	 * @memberof WellnessComponent
	 */
	private _detectMobile(): boolean {
		if (window.innerWidth <= 990) {
			return true;
		} else {
			return false;
		}
	}

	/**
	 * Getting data carrier service channel data
	 * - sets the current user state to this.state
	 * - if health data is available it will call _wellnessConfig to configure the application
	 * @private
	 * @memberof WellnessComponent
	 */
	private _getWellnessData(): void {
		this.dataCarrierService
			.getChannel()
			.pipe(takeUntil(this._destroyed$))
			.subscribe(carrierData => {
				const { panelStatus } = carrierData;
				if (panelStatus) {
					if (panelStatus.welcomePanel === 'NO_SCHEDULED_PE') {
						this.state = 0;
					} else if (panelStatus.welcomePanel === 'UPCOMING_PE') {
						this.state = 1;
					} else if (panelStatus.welcomePanel === 'PENDING_REPORTS') {
						this.state = 2;
					} else if (panelStatus.welcomePanel === 'REPORTS_IN') {
						this.state = 3;
					}
				}
			});
	}

	/**
	 *
	 * retreives the wellness / health portfolio data for a given user
	 * @private
	 * @memberof HeaderComponent
	 */
	private _getHealthSummary(): void {
		this.healthSummaryService.getHealthSummary().subscribe(
			healthData => {
				if (healthData) {
					if (this._isDataEmpty(healthData) === false) {
						const { data }: { data: Finding[] } = healthData;
						if (this._isDataEmpty(data) === false) {
							this._wellnessConfig(data);
						}
					}
				}
			},
			err => {
				console.error(`[${WellnessComponent.name}][${this._getHealthSummary.name}]`, 'An error occurred while fetching health summary data ', err);
			}
		);
	}

	/**
	 * This component needs health data to work but will then configure
	 * the component based on the data provided and then it will set:
	 *  - the data for the component properties
	 *  - the empty data checks
	 *  - isEmpty - if the service call fails or no data available
	 * @private
	 * @param {Finding[]} healthData
	 * @memberof WellnessComponent
	 */
	private _wellnessConfig(healthData: Finding[]): void {
		const filteredMetricArray = this._emptyResultFilter(healthData);
		if (filteredMetricArray.length > 0) {
			const healthMeasurementData_ = this._dataBuilder(filteredMetricArray, 'Health Measurements');
			const labResultsData_ = this._dataBuilder(filteredMetricArray, 'Lab Results');
			const lifestyleData_ = this._dataBuilder(filteredMetricArray, 'Lifestyle');
			this.healthMeasurementData = healthMeasurementData_;
			this.healthMeasurementDataEmpty = this._isDataEmpty(healthMeasurementData_);
			this.labResultsData = labResultsData_;
			this.labResultsDataEmpty = this._isDataEmpty(labResultsData_);
			this.lifestyleData = lifestyleData_;
			this.lifestyleDataEmpty = this._isDataEmpty(lifestyleData_);
			this.isEmpty = false;
		}
	}

	/**
	 *
	 * checks if the array is undefined or empty
	 * @private
	 * @param {Finding[]} data
	 * @returns {boolean}
	 * @memberof WellnessComponent
	 */
	private _isDataEmpty(data): boolean {
		if (data !== null && data !== undefined) {
			return false;
		} else {
			return true;
		}
	}

	/**
	 *
	 * Goes through an array of Findings and gives back questions that
	 * have been answered
	 * @private
	 * @param {Finding[]} healthData
	 * @returns {Finding[]}
	 * @memberof WellnessComponent
	 */
	private _emptyResultFilter(healthData: Finding[]): Finding[] {
		const filteredMetricArray: Finding[] = healthData
			.filter((finding: Finding) => finding.value !== 'Not Available' && finding.value !== 'Not Answered')
			.filter((finding: Finding) => finding.ranges.length > 0);
		return filteredMetricArray;
	}

	/**
	 *
	 * provides the data for a given section of the component
	 * @private
	 * @param {Finding[]} filteredMetricArray
	 * @param {string} section
	 * @returns {Finding[]}
	 * @memberof WellnessComponent
	 */
	private _dataBuilder(filteredMetricArray: Finding[], section: string): Finding[] {
		const data = filteredMetricArray.filter(metric => metric.category === section);
		if (data.length > 0) {
			return data;
			// return data.map(v => ({ ...v, description: v.description?.replace(/|/ig, `"`) }));
		}
		return undefined;
	}

	/**
	 * checking the router events to open/close the corresponding section
	 * @private
	 * @memberof WellnessComponent
	 */
	private _getActiveSection(): Subscription {
		return this.router.events.pipe(takeUntil(this._destroyed$)).subscribe((res: any) => {
			if (res instanceof NavigationEnd) {
				this._handleRouteChange(res.url);
			}
		});
	}

	/**
	 * based on current URL switches between sections if in web view:
	 * - activates the expansion panel
	 * @private
	 * @param {string[]} currentUrl the current user route
	 * @memberof WellnessComponent
	 */
	private _handleRouteChange(currentUrl: string): void {
		this.renderCharts = false;
		timer(250).subscribe(val => (this.renderCharts = true));
		if (this.isMobile === true) {
			this.mobileIndex = this._urlToIndex(currentUrl);
			this._mobileRouteChange(currentUrl);
		} else {
			this.activeSectionIndex = this._urlToIndex(currentUrl);
		}
	}

	/**
	 *
	 * provides the index for the given url
	 * @private
	 * @param {string} currentUrl
	 * @returns {number}
	 * @memberof WellnessComponent
	 */
	private _urlToIndex(currentUrl: string): number {
		switch (currentUrl) {
			case '/results/summary':
				return 0;
			case '/results/summary/health/measure':
				return 0;
			case '/results/summary/health/lab-results':
				return 1;
			case '/results/summary/health/lifestyle':
				return 2;
		}
	}

	/**
	 *
	 * sets the Dom Element ID to scroll to based on the
	 * given url
	 * @private
	 * @param {string} currentUrl
	 * @returns {(Observable<any> | NodeJS.Timer)}
	 * @memberof WellnessComponent
	 */
	private _mobileRouteChange(currentUrl: string): Observable<any> | NodeJS.Timer {
		switch (currentUrl) {
			case '/wellness/summary':
				return this._isfirstScroll('measure');
			case '/wellness/summary/health/measure':
				return this._isfirstScroll('measure');
			case '/wellness/summary/health/lab-results':
				return this._isfirstScroll('lab-results');
			case '/wellness/summary/health/lifestyle':
				return this._isfirstScroll('lifestyle');
		}
	}

	/**
	 * Expansion panel functionality
	 * Expand all panels
	 * @memberof WellnessComponent
	 */
	openAll(): void {
		this.myPanels.openAll();
	}

	/**
	 * Event Emitting from app-responsive-menu component
	 * @param selectedIndex - user selected section's index
	 * @memberof WellnessComponent
	 */
	getSelectedIndex(selectedIndex: number): void {
		if (this.isEmpty !== true) {
			this.activeSectionIndex = selectedIndex;
		}
	}

	/**
	 *
	 * Checks if this is the first load of the webpage
	 * This is done by checking if the data is empty because
	 * active section is called in the constructor
	 * @private
	 * @param {string} sectionId
	 * @returns {(Observable<any> | NodeJS.Timer)}
	 * @memberof WellnessComponent
	 */
	private _isfirstScroll(sectionId: string): Observable<any> | NodeJS.Timer {
		if (this.isEmpty === true) {
			return setTimeout(() => {
				this._scrollToSection(sectionId);
			}, 1000);
		} else {
			return this._scrollToSection(sectionId);
		}
	}

	/**
	 * scrolling to the corresponding section based on dom ID
	 * - This functionality has been restricted for mobile devices and
	 * is only called by mobile related methods
	 * @private
	 * @param sectionId - Dom Element ID
	 * @memberof WellnessComponent
	 */
	private _scrollToSection(sectionId: string): Observable<any> {
		// FIXME: currently scrolls from the top instead of from the relative section
		const scrollConfig = {
			target: sectionId,
			duration: 1000,
			offset: -50,
		};
		return this._scrollToService.scrollTo(scrollConfig);
	}

	/**
	 * Cleans the process
	 * @memberof WellnessComponent
	 */
	ngOnDestroy(): void {
		this._destroyed$.next(true);
		this._destroyed$.unsubscribe();
	}
}
